import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import { DateInputWithShortcut } from "../DateInputWithShortcut";
import type { CreateRevenueData } from "../../../services/api/financial.service";

export interface RevenueFormProps {
  onSubmit: (data: CreateRevenueData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onRevise?: () => void;
  canRevise?: boolean;
  initialData?: Partial<CreateRevenueData>;
  isLoading?: boolean;
  isEditing?: boolean;
  startupStartDate?: Date | string | null;
}

const REVENUE_CATEGORIES = [
  "sales",
  "grant",
  "other",
] as const;

const REVENUE_COLORS = [
  "#10b981", // green
  "#3b82f6", // blue
  "#8b5cf6", // purple
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
];

export const RevenueForm: React.FC<RevenueFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  onRevise,
  canRevise = false,
  initialData,
  isLoading = false,
  isEditing = false,
  startupStartDate,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateRevenueData>({
    defaultValues: {
      name: initialData?.name || "",
      category: initialData?.category || "sales",
      monthlyAmount: initialData?.monthlyAmount || 0,
      startDate:
        initialData?.startDate || new Date().toISOString().split("T")[0],
      endDate: initialData?.endDate || "",
      isRecurring: initialData?.isRecurring ?? true,
      dayOfMonth: initialData?.dayOfMonth || 1,
      frequency: initialData?.frequency || "monthly",
      color: initialData?.color || REVENUE_COLORS[0],
      // New growth field - convert from decimal to percentage for display
      growthRate: initialData?.growthRate ? initialData.growthRate * 100 : 0,
    },
  });

  const selectedCategory = watch("category");

  // Auto-assign color based on category
  React.useEffect(() => {
    if (selectedCategory && !initialData?.color) {
      const categoryIndex = REVENUE_CATEGORIES.indexOf(selectedCategory);
      const colorIndex =
        categoryIndex >= 0 ? categoryIndex % REVENUE_COLORS.length : 0;
      setValue("color", REVENUE_COLORS[colorIndex]);
    }
  }, [selectedCategory, initialData, setValue]);

  const handleFormSubmit = (data: CreateRevenueData) => {
    // Convert empty endDate to undefined and ensure numeric fields are numbers
    const processedData = {
      ...data,
      monthlyAmount: Number(data.monthlyAmount),
      dayOfMonth: Number(data.dayOfMonth),
      growthRate: data.growthRate !== undefined && data.growthRate !== null ? Number(data.growthRate) / 100 : undefined, // Convert percentage to decimal
      endDate: data.endDate || undefined,
    };
    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormField label="Revenue Name" error={errors.name?.message}>
        <Input
          {...register("name", { required: "Revenue name is required" })}
          placeholder="e.g., Monthly Subscriptions, Consulting Services"
        />
      </FormField>

      <FormField label="Category" error={errors.category?.message}>
        <select
          {...register("category", { required: "Category is required" })}
          className="sh-select"
        >
          <option value="sales">Sales</option>
          <option value="grant">Grant</option>
          <option value="other">Other</option>
        </select>
      </FormField>

      <FormField
        label="Monthly Amount ($)"
        error={errors.monthlyAmount?.message}
      >
        <Input
          type="number"
          step="0.01"
          {...register("monthlyAmount", {
            required: "Monthly amount is required",
            min: { value: 0, message: "Amount must be positive" },
            valueAsNumber: true,
          })}
          placeholder="0.00"
        />
      </FormField>

      <FormField
        label="Monthly Growth Rate (%)"
        error={errors.growthRate?.message}
      >
        <Input
          type="number"
          step="0.01"
          {...register("growthRate", {
            min: {
              value: -100,
              message: "Growth rate cannot be less than -100%",
            },
            valueAsNumber: true,
          })}
          placeholder="0.00"
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Start Date" error={errors.startDate?.message}>
          <DateInputWithShortcut
            shortcutDate={startupStartDate}
            {...register("startDate", { required: "Start date is required" })}
            onUseShortcut={(value) =>
              setValue("startDate", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </FormField>

        <FormField label="End Date (Optional)" error={errors.endDate?.message}>
          <Input type="date" {...register("endDate")} />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Frequency" error={errors.frequency?.message}>
          <select
            {...register("frequency", { required: "Frequency is required" })}
            className="sh-select"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </FormField>

        <FormField label="Day of Month" error={errors.dayOfMonth?.message}>
          <Input
            type="number"
            min="1"
            max="31"
            {...register("dayOfMonth", {
              min: { value: 1, message: "Must be between 1-31" },
              max: { value: 31, message: "Must be between 1-31" },
              valueAsNumber: true,
            })}
            placeholder="1"
          />
        </FormField>
      </div>

      <FormField label="Recurring Revenue" error={errors.isRecurring?.message}>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            {...register("isRecurring")}
            className="w-4 h-4 text-brand bg-white/[0.06] border-white/10 rounded focus:ring-brand/40"
          />
          <label className="text-sm text-white/75">
            This is recurring revenue
          </label>
        </div>
      </FormField>

      <FormField label="Color" error={errors.color?.message}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            {...register("color")}
            className="w-12 h-10 border border-white/10 rounded cursor-pointer"
          />
          <Input
            {...register("color")}
            placeholder="#10b981"
            className="flex-1"
          />
        </div>
      </FormField>

      {isEditing && canRevise && onRevise ? (
        <FormHelpCallout>
          Changing amount or growth here rewrites history. To keep past months as
          modeled,{" "}
          <button
            type="button"
            className="font-medium text-sky-300 underline underline-offset-2"
            onClick={onRevise}
          >
            revise from a date
          </button>{" "}
          instead.
        </FormHelpCallout>
      ) : null}

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
        {isEditing && onDelete && (
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onDelete}
            disabled={isLoading}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};